import React, { useState, useEffect } from 'react';

const TIMEZONES = [
  { value: 'Asia/Bangkok', label: '🇹🇭 เอเชีย/กรุงเทพฯ (UTC+7)', offset: '+07:00' },
  { value: 'Asia/Tokyo', label: '🇯🇵 เอเชีย/โตเกียว (UTC+9)', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: '🇨🇳 เอเชีย/เซี่ยงไฮ้ (UTC+8)', offset: '+08:00' },
  { value: 'Asia/Singapore', label: '🇸🇬 เอเชีย/สิงคโปร์ (UTC+8)', offset: '+08:00' },
  { value: 'Asia/Ho_Chi_Minh', label: '🇻🇳 เอเชีย/โฮจิมินห์ (UTC+7)', offset: '+07:00' },
  { value: 'Asia/Yangon', label: '🇲🇲 เอเชีย/ย่างกุ้ง (UTC+6:30)', offset: '+06:30' },
  { value: 'America/New_York', label: '🇺🇸 อเมริกา/นิวยอร์ก (UTC-5)', offset: '-05:00' },
  { value: 'Europe/London', label: '🇬🇧 ยุโรป/ลอนดอน (UTC+0)', offset: '+00:00' },
  { value: 'Europe/Berlin', label: '🇩🇪 ยุโรป/เบอร์ลิน (UTC+1)', offset: '+01:00' },
  { value: 'Australia/Sydney', label: '🇦🇺 ออสเตรเลีย/ซิดนีย์ (UTC+10)', offset: '+10:00' },
];

const NTP_SERVERS = [
  { value: '1.th.pool.ntp.org', label: '1.th.pool.ntp.org (ไทย)', country: '🇹🇭' },
  { value: '2.th.pool.ntp.org', label: '2.th.pool.ntp.org (ไทย)', country: '🇹🇭' },
  { value: '3.th.pool.ntp.org', label: '3.th.pool.ntp.org (ไทย)', country: '🇹🇭' },
  { value: '0.asia.pool.ntp.org', label: '0.asia.pool.ntp.org (เอเชีย)', country: '🌏' },
  { value: '1.asia.pool.ntp.org', label: '1.asia.pool.ntp.org (เอเชีย)', country: '🌏' },
  { value: 'pool.ntp.org', label: 'pool.ntp.org (ทั่วโลก)', country: '🌍' },
  { value: 'time.google.com', label: 'Google NTP', country: '🔵' },
  { value: 'time.apple.com', label: 'Apple NTP', country: '🍎' },
  { value: 'time.nist.gov', label: 'NIST (สหรัฐฯ)', country: '🇺🇸' },
];

const DEFAULTS = {
  timezone: 'Asia/Bangkok',
  ntp_enabled: true,
  ntp_servers: ['1.th.pool.ntp.org', '2.th.pool.ntp.org', '3.th.pool.ntp.org'],
  ntp_interval: '1h',
  custom_ntp: '',
};

export default function StepTimeNTP({ data, onChange, onNext, onBack }) {
  const [form, setForm] = useState(data || DEFAULTS);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const toggleNtpServer = (server) => {
    const servers = form.ntp_servers || [];
    const updated = servers.includes(server)
      ? servers.filter(s => s !== server)
      : [...servers, server];
    update('ntp_servers', updated);
  };

  const addCustomNtp = () => {
    if (!form.custom_ntp.trim()) return;
    const servers = form.ntp_servers || [];
    update('ntp_servers', [...servers, form.custom_ntp.trim()]);
    update('custom_ntp', '');
  };

  const removeNtpServer = (index) => {
    const servers = form.ntp_servers.filter((_, i) => i !== index);
    update('ntp_servers', servers);
  };

  const currentTZ = TIMEZONES.find(t => t.value === form.timezone);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🕐 ตั้งค่าเวลาและ NTP</h2>
        <p className="text-gray-500 dark:text-gray-400">ตั้งค่าเขตเวลา (Time Zone) และเซิร์ฟเวอร์ NTP เพื่อให้ Router มีเวลาที่ถูกต้อง</p>
      </div>

      {/* Time Zone */}
      <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <span className="text-xl">🌍</span> เขตเวลา (Time Zone)
        </h3>
        <div className="space-y-3">
          <div>
            <label className="label">เลือกเขตเวลา</label>
            <select value={form.timezone} onChange={e => update('timezone', e.target.value)} className="input-field">
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          {currentTZ && (
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">UTC{currentTZ.offset}</span>
                <span className="text-xs text-indigo-500 dark:text-indigo-400">{currentTZ.value}</span>
              </div>
              <div className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                เวลาปัจจุบันในเขตนี้: {new Date().toLocaleString('th-TH', { timeZone: form.timezone })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* NTP Client */}
      <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-xl">⏰</span> NTP Client (ซิงค์เวลาอัตโนมัติ)
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-gray-600 dark:text-gray-400">เปิดใช้งาน</span>
            <input type="checkbox" checked={form.ntp_enabled} onChange={e => update('ntp_enabled', e.target.checked)} className="w-5 h-5 text-indigo-600 rounded" />
          </label>
        </div>

        {form.ntp_enabled && (
          <div className="space-y-4">
            {/* Preset NTP Servers */}
            <div>
              <label className="label">เลือกเซิร์ฟเวอร์ NTP</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {NTP_SERVERS.map(server => {
                  const isSelected = (form.ntp_servers || []).includes(server.value);
                  return (
                    <button
                      key={server.value}
                      onClick={() => toggleNtpServer(server.value)}
                      className={`p-2.5 rounded-lg border-2 text-left text-xs transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <span className="mr-1">{server.country}</span>
                      <span className="font-medium">{server.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected NTP Servers */}
            {form.ntp_servers && form.ntp_servers.length > 0 && (
              <div>
                <label className="label">เซิร์ฟเวอร์ที่เลือก ({form.ntp_servers.length} ตัว)</label>
                <div className="space-y-1.5">
                  {form.ntp_servers.map((server, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800/50">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-indigo-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                        <span className="text-sm font-mono text-indigo-700 dark:text-indigo-300">{server}</span>
                      </div>
                      <button onClick={() => removeNtpServer(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom NTP */}
            <div className="flex gap-2">
              <input
                type="text"
                value={form.custom_ntp}
                onChange={e => update('custom_ntp', e.target.value)}
                className="input-field text-sm flex-1"
                placeholder="เพิ่มเซิร์ฟเวอร์ NTP เอง (เช่น ntp.example.com)"
              />
              <button onClick={addCustomNtp} className="btn-primary text-sm whitespace-nowrap">เพิ่ม</button>
            </div>

            {/* Sync Interval */}
            <div>
              <label className="label">ความถี่ในการซิงค์</label>
              <select value={form.ntp_interval} onChange={e => update('ntp_interval', e.target.value)} className="input-field">
                <option value="1m">ทุก 1 นาที</option>
                <option value="5m">ทุก 5 นาที</option>
                <option value="15m">ทุก 15 นาที</option>
                <option value="1h">ทุก 1 ชั่วโมง</option>
                <option value="6h">ทุก 6 ชั่วโมง</option>
                <option value="1d">ทุก 1 วัน</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="mb-6 p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/50 rounded-lg">
        <p className="text-xs text-indigo-700 dark:text-indigo-300">
          <strong>💡 เคล็ดลับ:</strong> การซิงค์เวลากับ NTP Server ช่วยให้ Log, Schedule, Certificate และ Hotspot ทำงานถูกต้อง แนะนำให้เลือกเซิร์ฟเวอร์ NTP ในประเทศ (ไทย) เพื่อความเร็วสูงสุด
        </p>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป</button>
      </div>
    </div>
  );
}
